import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useParams, useNavigate } from 'react-router';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { AuthContext } from 'components/authentication/context/authContext';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';

interface LinkedInvoice {
  id: number;
  number: string;
  date: string;
  from: string;
  to: string;
  amount: number;
  currency?: { code: string };
}

interface UnreconciledInvoice {
  id: number;
  number: string;
  date: string;
  from: string;
  to: string;
  amount: number;
  currency?: { code: string };
}

interface SeatInfo {
  id: number;
  amount: number;   // cents
  incoming: number;
  date: string;
  description: string;
  account?: { name: string; accountNumber: string };
  currency?: { code: string };
}

const ReconciliationPage: React.FC = () => {
  const { accountId, seatId } = useParams<{ accountId: string; seatId: string }>();
  const navigate = useNavigate();
  const api: Api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const { success: toastSuccess, error: toastError } = useToastMessageQueue();

  const parsedSeatId = parseInt(seatId || '0', 10);
  const parsedAccountId = parseInt(accountId || '0', 10);

  // Seat info
  const [seat, setSeat] = useState<SeatInfo | null>(null);
  const [seatLoading, setSeatLoading] = useState(true);

  // Already-linked invoices
  const [linkedInvoices, setLinkedInvoices] = useState<LinkedInvoice[]>([]);
  const [linkedLoading, setLinkedLoading] = useState(true);

  // Unreconciled invoice search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UnreconciledInvoice[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Actions
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const formatCurrency = (amount: number, code = '') => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: code || 'USD' }).format(amount);
    } catch {
      return `${code} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    }
  };

  // Fetch the seat details
  const loadSeat = useCallback(async () => {
    if (!parsedSeatId) return;
    setSeatLoading(true);
    try {
      const res = await (api.invoiceApi as any).apiVversionLedgerIdGet(parsedSeatId, '1');
      setSeat(res.data?.data ?? null);
    } catch {
      setSeat(null);
    } finally {
      setSeatLoading(false);
    }
  }, [api, parsedSeatId]);

  // Fetch already-linked invoices
  const loadLinked = useCallback(async () => {
    if (!parsedSeatId) return;
    setLinkedLoading(true);
    try {
      const res = await api.invoiceApiWrapper.getSeatReconciliation('1', parsedSeatId);
      setLinkedInvoices(res.data?.data ?? []);
    } catch {
      setLinkedInvoices([]);
    } finally {
      setLinkedLoading(false);
    }
  }, [api, parsedSeatId]);

  useEffect(() => {
    loadSeat();
    loadLinked();
  }, [loadSeat, loadLinked]);

  // Debounced unreconciled search
  const searchUnreconciled = useCallback(async (term: string) => {
    setSearchLoading(true);
    try {
      const res = await api.invoiceApiWrapper.searchPaidUnreconciled('1', term, 1, 50);
      setSearchResults(res.data?.data?.queryResult ?? []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [api]);

  useEffect(() => {
    const timer = setTimeout(() => searchUnreconciled(searchTerm), 350);
    return () => clearTimeout(timer);
  }, [searchTerm, searchUnreconciled]);

  const handleToggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAddSelected = async () => {
    if (selectedIds.size === 0) return;
    setSaving(true);
    try {
      await api.invoiceApiWrapper.reconcileSeat('1', parsedSeatId, Array.from(selectedIds));
      toastSuccess({ message: intl.get('reconciliation.linked') || 'Invoices linked successfully.' });
      setSelectedIds(new Set());
      await loadLinked();
      // Remove just-linked items from search results
      setSearchResults((prev) => prev.filter((r) => !selectedIds.has(r.id)));
    } catch {
      toastError({ message: intl.get('reconciliation.errorLinking') || 'Failed to link invoices.' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLinked = async (invoiceId: number) => {
    setRemovingId(invoiceId);
    try {
      await api.invoiceApiWrapper.unreconcileSeat('1', parsedSeatId, invoiceId);
      toastSuccess({ message: intl.get('reconciliation.unlinked') || 'Invoice unlinked.' });
      setLinkedInvoices((prev) => prev.filter((i) => i.id !== invoiceId));
    } catch {
      toastError({ message: intl.get('reconciliation.errorUnlinking') || 'Failed to unlink invoice.' });
    } finally {
      setRemovingId(null);
    }
  };

  // Derived amounts
  const seatAmountDecimal = seat ? seat.amount / 100 : 0;
  const allocatedAmount = linkedInvoices.reduce((sum, inv) => sum + (inv.amount ?? 0), 0);
  const remainingAmount = seatAmountDecimal - allocatedAmount;
  const currencyCode = seat?.currency?.code || '';

  const allocationPct = seatAmountDecimal > 0
    ? Math.min(100, (allocatedAmount / seatAmountDecimal) * 100)
    : 0;

  if (seatLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!seat) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {intl.get('reconciliation.seatNotFound') || 'Ledger entry not found.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <Icon icon="mdi:bank-check" width={24} />
          <Typography variant="h6">
            {intl.get('reconciliation.title') || 'Reconciliation'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Icon icon="mdi:arrow-left" />}
          onClick={() => navigate(`/account/detail/${parsedAccountId}`)}
        >
          {intl.get('common.back') || 'Back'}
        </Button>
      </Box>

      {/* Seat summary card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {intl.get('reconciliation.ledgerEntry') || 'Ledger Entry'}
          </Typography>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Chip
              label={seat.incoming === 1
                ? (intl.get('ledgerLink.credit') || 'Credit')
                : (intl.get('ledgerLink.debit') || 'Debit')}
              color={seat.incoming === 1 ? 'success' : 'error'}
              size="small"
            />
            <Typography variant="h5" fontWeight="bold" fontFamily="monospace">
              {formatCurrency(seatAmountDecimal, currencyCode)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {new Date(seat.date).toLocaleDateString()}
            </Typography>
            {seat.account && (
              <Typography variant="body2" color="text.secondary">
                {seat.account.name} ({seat.account.accountNumber})
              </Typography>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {seat.description}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Allocation progress */}
          <Box>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" color="text.secondary">
                {intl.get('reconciliation.allocated') || 'Allocated'}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(allocatedAmount, currencyCode)}
                {' / '}
                {formatCurrency(seatAmountDecimal, currencyCode)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={allocationPct}
              color={allocationPct >= 100 ? 'success' : 'primary'}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Box display="flex" justifyContent="flex-end" mt={0.5}>
              <Typography
                variant="caption"
                color={remainingAmount < 0 ? 'error.main' : 'text.secondary'}
              >
                {intl.get('reconciliation.remaining') || 'Remaining:'}
                {' '}
                {formatCurrency(remainingAmount, currencyCode)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Linked invoices */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
            <Icon icon="mdi:file-check-outline" width={20} />
            {intl.get('reconciliation.linkedInvoices') || 'Linked Invoices'}
            <Chip label={linkedInvoices.length} size="small" />
          </Typography>

          {linkedLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : linkedInvoices.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              {intl.get('reconciliation.noLinkedInvoices') || 'No invoices linked yet.'}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{intl.get('invoice.number') || 'Number'}</TableCell>
                  <TableCell>{intl.get('invoice.date') || 'Date'}</TableCell>
                  <TableCell>{intl.get('invoice.from') || 'From'}</TableCell>
                  <TableCell align="right">{intl.get('invoice.amount') || 'Amount'}</TableCell>
                  <TableCell width={48} />
                </TableRow>
              </TableHead>
              <TableBody>
                {linkedInvoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">{inv.number}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(inv.date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{inv.from}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                        {formatCurrency(inv.amount, inv.currency?.code || currencyCode)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={intl.get('reconciliation.removeLink') || 'Remove link'}>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={removingId === inv.id}
                          onClick={() => handleRemoveLinked(inv.id)}
                        >
                          {removingId === inv.id
                            ? <CircularProgress size={16} />
                            : <Icon icon="mdi:link-variant-off" width={16} />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Search and add unreconciled invoices */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
            <Icon icon="mdi:file-search-outline" width={20} />
            {intl.get('reconciliation.addInvoices') || 'Add Invoices'}
          </Typography>

          <TextField
            size="small"
            fullWidth
            placeholder={intl.get('reconciliation.searchPlaceholder') || 'Search by invoice number, vendor, or amount'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Icon icon="mdi:magnify" width={18} style={{ marginRight: 8 }} />,
              endAdornment: searchLoading ? <CircularProgress size={16} /> : null,
            }}
            sx={{ mb: 2 }}
          />

          {searchResults.length === 0 && !searchLoading ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              {intl.get('reconciliation.noUnreconciled') || 'No unreconciled paid invoices found.'}
            </Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        indeterminate={selectedIds.size > 0 && selectedIds.size < searchResults.length}
                        checked={searchResults.length > 0 && selectedIds.size === searchResults.length}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedIds(new Set(searchResults.map((r) => r.id)));
                          else setSelectedIds(new Set());
                        }}
                      />
                    </TableCell>
                    <TableCell>{intl.get('invoice.number') || 'Number'}</TableCell>
                    <TableCell>{intl.get('invoice.date') || 'Date'}</TableCell>
                    <TableCell>{intl.get('invoice.from') || 'From'}</TableCell>
                    <TableCell align="right">{intl.get('invoice.amount') || 'Amount'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((inv) => (
                    <TableRow
                      key={inv.id}
                      hover
                      selected={selectedIds.has(inv.id)}
                      onClick={() => handleToggleSelect(inv.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selectedIds.has(inv.id)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">{inv.number}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(inv.date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>{inv.from}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                          {formatCurrency(inv.amount, inv.currency?.code || currencyCode)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {selectedIds.size > 0 && (
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Icon icon="mdi:link-variant-plus" />}
                    disabled={saving}
                    onClick={handleAddSelected}
                  >
                    {intl.get('reconciliation.linkSelected') || `Link ${selectedIds.size} invoice(s)`}
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ReconciliationPage;
